<script setup>
import { until } from '@vueuse/core'
import { useCalendarBus } from '~/schedule/composables/useCalendarBus'
import { useCalendarViewState } from '~/schedule/composables/useCalendarViewState'

const props = defineProps({
  date: {
    type: String,
  },
})
const calendarBus = useCalendarBus()
const { t } = useI18n()
const container = ref(null)
const highlighted = ref(false)
const view = useCalendarViewState()

calendarBus.on('highlight', async (date) => {
  if (props.date && date === props.date) {
    await until(container).toBeTruthy()
    container.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    highlighted.value = true
    setTimeout(() => (highlighted.value = false), 3000)
  }
})
// day view does not apply, no event alert shown instead
const showNoEventsText = computed(() => view.value === 'month')
</script>

<template>
  <div
    ref="container"
    class="schedule-event-container"
    :class="{ 'w-[calc(100%/7-1px)]': view === 'week' }"
  >
    <div
      class="calendar-event-container-inner highlight flex flex-1 flex-col flex-gap-2 rounded bg-white p-2 ring-white ring-inset"
      :class="{ 'highlight-on ring-2 !ring-primary': highlighted }"
    >
      <div
        v-if="$slots.caption"
        class="caption"
      >
        <slot name="caption" />
      </div>
      <slot>
        <div
          v-if="showNoEventsText"
          class="empty-container"
        >
          <div class="empty-event">
            {{ t('No Events') }}
          </div>
        </div>
      </slot>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.schedule-event-container {
  @apply bg-white flex flex-col flex-1;

  .caption {
    @apply pointer-events-none text-right text-text-500;
  }

  .empty-container {
    @apply flex flex-1 justify-center items-center min-h-54px;
  }

  .empty-event {
    @apply text-sm bg-surface-300 rounded p-block-2 p-inline-4 text-text-400 text-center;
  }

  .closed-container {
    @apply flex flex-1 justify-center items-center min-h-100px opactiy-80 b-rounded-2;
    background: repeating-linear-gradient(
      -45deg,
      var(--colors-surface-600),
      var(--colors-surface-600) 3px,
      var(--colors-surface-100) 3px,
      var(--colors-surface-100) 5.5px
    );
  }
  .closed-event {
    @apply text-sm bg-surface-700 rounded p-block-1 p-inline-2 text-text-600 text-center;
  }
}
</style>

<i18n>
{
  "en-us": {
    "No Events": "No Events",
    "Closed": "Closed"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "No Events": "Keine Veranstaltungen",
  "Closed": "Geschlossen"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "No Events": "Aucun événement",
  "Closed": "Clôturé(e)"
}
</i18n>

<i18n locale="es" lang="json">
{
  "No Events": "No hay eventos",
  "Closed": "Cerrado"
}
</i18n>
